import { useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Outlet, NavLink, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Bell,
  ClipboardList,
  CreditCard,
  FolderOpen,
  Heart,
  HeartPulse,
  LayoutDashboard,
  Pill,
  Settings,
  Stethoscope,
  Syringe,
  Cpu,
  User,
  Users,
  AlertCircle,
  Search,
  Command,
  ScrollText,
  type LucideIcon,
} from "lucide-react";
import { calculateAge, cn } from "@/lib/utils";
import { isHiddenChartTab } from "@/lib/hidden-features";
import { cleanClinicalLabel, dedupeAllergiesBySubstance } from "@/lib/clinical-display";
import { useAuth } from "@/hooks/use-auth";
import { FloatingHeader } from "@/components/layout/PracticeLayout";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePatient } from "@/hooks/use-patients";
import { useQuery } from "@tanstack/react-query";
import { getActiveAnnouncements } from "@/api/settings.api";
import { useActiveAllergies } from "@/hooks/use-allergies";
import EmmaPhoto from "@/assets/demo/emma-thompson.png";
import { ShellPageTransition } from "@/components/layout/shell";
import {
  WorkbenchFloatingDock,
  WorkbenchTabBarSlot,
} from "@/components/layout/workbench";
import { PatientAnnouncementBanner } from "@/components/patients/patient-announcement-banner";
import { CoverageCard } from "@/components/corporate/bits";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function patientDisplayName(patient?: {
  first_name?: string;
  last_name?: string;
} | null) {
  if (!patient) return null;
  const last = patient.last_name?.trim() ?? "";
  const first = patient.first_name?.trim() ?? "";
  if (last && first) return `${last}, ${first}`;
  return last || first || null;
}

function patientAgeLabel(dob?: string | null) {
  if (!dob) return null;
  const age = calculateAge(dob);
  if (!Number.isFinite(age) || age < 0) return null;
  return `${age} yr${age === 1 ? "" : "s"}`;
}

// ── Nav definition ────────────────────────────────────────────────────────────
interface NavItem {
  label: string;
  shortLabel: string;
  icon: LucideIcon;
  path: string;
  end?: boolean;
}
interface NavSection {
  label: string;
  items: NavItem[];
}

const ALL_CHART_NAV: NavSection[] = [
  {
    label: "Overview",
    items: [{ label: "Summary", shortLabel: "Home", icon: LayoutDashboard, path: "", end: true }],
  },
  {
    label: "Clinical",
    items: [
      { label: "Encounters", shortLabel: "Encounter", icon: Stethoscope, path: "encounters" },
      { label: "Vitals", shortLabel: "Vitals", icon: HeartPulse, path: "vitals" },
      { label: "Allergies", shortLabel: "Allergy", icon: AlertCircle, path: "allergies" },
      { label: "Conditions", shortLabel: "Dx", icon: Heart, path: "conditions" },
      { label: "Medications", shortLabel: "Meds", icon: Pill, path: "medications" },
      { label: "Prescriptions", shortLabel: "Rx", icon: ScrollText, path: "prescriptions" },
      { label: "Vaccines", shortLabel: "Vax", icon: Syringe, path: "immunizations" },
      { label: "Implants", shortLabel: "Devices", icon: Cpu, path: "devices" },
      { label: "Demographics", shortLabel: "Info", icon: User, path: "demographics" },
    ],
  },
  {
    label: "Care",
    items: [
      { label: "Documents", shortLabel: "Docs", icon: FolderOpen, path: "documents" },
      { label: "Care plans", shortLabel: "Plans", icon: ClipboardList, path: "care-plans" },
      { label: "Care team", shortLabel: "Team", icon: Users, path: "care-team" },
      { label: "Follow-ups", shortLabel: "Recall", icon: Bell, path: "recall" },
    ],
  },
  {
    label: "Admin",
    items: [
      { label: "Billing", shortLabel: "Bill", icon: CreditCard, path: "billing" },
      { label: "Settings", shortLabel: "Setup", icon: Settings, path: "settings" },
    ],
  },
];

// Billing / prescriptions tabs are hidden for Orange (see lib/hidden-features).
const CHART_NAV: NavSection[] = ALL_CHART_NAV.map((section) => ({
  ...section,
  items: section.items.filter((item) => !isHiddenChartTab(item.path)),
})).filter((section) => section.items.length > 0);

// ── Nav link ──────────────────────────────────────────────────────────────────
function ChartNavLink({
  href,
  end,
  icon: Icon,
  label,
  compact,
}: {
  href: string;
  end?: boolean;
  icon: LucideIcon;
  label: string;
  /** Horizontal pill for the small-screen strip. */
  compact?: boolean;
}) {
  return (
    <NavLink
      to={href}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-full text-sm font-medium transition-colors",
          compact ? "h-9 shrink-0 gap-2 px-3.5" : "h-10 px-3",
          isActive
            ? "bg-[#003a43] text-white dark:bg-primary dark:text-primary-foreground"
            : compact
              ? "bg-card text-foreground/75"
              : "text-foreground/75 hover:bg-muted hover:text-foreground",
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={cn("h-4 w-4 shrink-0", isActive ? "text-[#ff8d6e]" : "text-muted-foreground")} />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function chartHref(id: string, path: string) {
  return path ? `/patients/${id}/${path}` : `/patients/${id}`;
}

// ── Patient data shared by the side card and the patient bar ─────────────────
function usePatientChartSummary(id: string) {
  const { data: patient } = usePatient(id);
  const { data: allergies } = useActiveAllergies(id);
  const { data: activeAnnouncements = [] } = useQuery({
    queryKey: ["announcements", id, "active"],
    queryFn: () => getActiveAnnouncements(id),
    enabled: !!id,
    staleTime: 60_000,
  });
  const uniqueAllergies = useMemo(() => dedupeAllergiesBySubstance(allergies ?? []), [allergies]);

  return {
    patient,
    uniqueAllergies,
    activeAnnouncements,
    displayName: patientDisplayName(patient),
    ageLabel: patientAgeLabel(patient?.date_of_birth),
    mrnLabel: patient?.mrn?.trim() || null,
    initials: `${patient?.first_name?.[0] ?? ""}${patient?.last_name?.[0] ?? ""}`.toUpperCase() || "?",
    photoSrc: patient?.photo_url || (patient?.mrn === "MRN-PX-001" ? EmmaPhoto : undefined),
  };
}

// ── Side column: patient card + chart menu ───────────────────────────────────
function ChartSideColumn({ id }: { id: string }) {
  const navigate = useNavigate();
  const { uniqueAllergies, activeAnnouncements, displayName, ageLabel, mrnLabel, initials, photoSrc } =
    usePatientChartSummary(id);

  return (
    <aside className="hidden w-64 shrink-0 flex-col gap-2 overflow-y-auto scrollbar-none lg:flex">
      {/* Patient card */}
      <div className="rounded-[24px] bg-card p-4 shadow-[0_1px_2px_rgba(0,58,67,0.05)]">
        <button
          type="button"
          onClick={() => navigate("/patients")}
          className="-ml-1 mb-3 flex h-8 items-center gap-1.5 rounded-full px-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          All patients
        </button>
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#ff8d6e] text-sm font-semibold text-[#003a43]">
            {photoSrc ? <img src={photoSrc} alt="" className="h-full w-full object-cover" /> : initials}
          </span>
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold leading-tight text-foreground">{displayName ?? "Loading…"}</p>
            <p className="mt-1 truncate text-xs text-muted-foreground">
              {[ageLabel, mrnLabel].filter(Boolean).join(" · ") || "Opening record…"}
            </p>
          </div>
        </div>
        <CoverageCard patientId={id} />
        {(uniqueAllergies.length > 0 || activeAnnouncements.length > 0) && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {uniqueAllergies.length > 0 && (
              <button
                type="button"
                onClick={() => navigate(`/patients/${id}/allergies`)}
                className="inline-flex items-center gap-1 rounded-full bg-[#ffe9e1] px-2.5 py-1 text-xs font-semibold text-[#c4532f] transition-colors hover:bg-[#ffdccf] dark:bg-[#ff8d6e]/15"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                {uniqueAllergies.length} allerg{uniqueAllergies.length === 1 ? "y" : "ies"}
              </button>
            )}
            {activeAnnouncements.length > 0 && (
              <button
                type="button"
                onClick={() => navigate(`/patients/${id}/settings?tab=announcements`)}
                className="inline-flex items-center gap-1 rounded-full bg-[#e6f0ee] px-2.5 py-1 text-xs font-semibold text-[#004952] transition-colors hover:bg-[#d8e8e5] dark:bg-primary/15 dark:text-primary"
              >
                <Bell className="h-3.5 w-3.5" />
                {activeAnnouncements.length} alert{activeAnnouncements.length !== 1 ? "s" : ""}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Chart menu */}
      <nav className="rounded-[24px] bg-card p-3 shadow-[0_1px_2px_rgba(0,58,67,0.05)]" aria-label="Patient chart">
        {CHART_NAV.map((section, index) => (
          <div key={section.label} className={cn(index > 0 && "mt-3")}>
            <p className="px-3 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {section.label}
            </p>
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <ChartNavLink
                  key={item.path || "summary"}
                  href={chartHref(id, item.path)}
                  end={item.end}
                  icon={item.icon}
                  label={item.label}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Allergy reminder */}
      {uniqueAllergies.length > 0 && (
        <div className="rounded-[24px] bg-[#ffe9e1] p-4 dark:bg-[#ff8d6e]/15">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#c4532f]">
            <AlertCircle className="h-3.5 w-3.5" />
            Allergies
          </p>
          <ul className="mt-2 space-y-1">
            {uniqueAllergies.slice(0, 3).map((a) => (
              <li key={a.id} className="truncate text-sm font-medium text-[#7a3218] dark:text-foreground" title={cleanClinicalLabel(a.allergen_display)}>
                {cleanClinicalLabel(a.allergen_display)}
              </li>
            ))}
          </ul>
          {uniqueAllergies.length > 3 && (
            <button
              type="button"
              onClick={() => navigate(`/patients/${id}/allergies`)}
              className="mt-2 text-xs font-semibold text-[#c4532f] hover:underline"
            >
              +{uniqueAllergies.length - 3} more
            </button>
          )}
        </div>
      )}
    </aside>
  );
}

// ── Patient bar (top of the content panel) ────────────────────────────────────
function ChartPatientBar({ id }: { id: string }) {
  const navigate = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const { patient, uniqueAllergies, activeAnnouncements, displayName, ageLabel, mrnLabel, initials, photoSrc } =
    usePatientChartSummary(id);
  const alertCount = activeAnnouncements.length + uniqueAllergies.length;

  const chartTargets = useMemo(
    () =>
      CHART_NAV.flatMap((section) =>
        section.items.map((item) => ({
          id: item.path || "summary",
          label: item.label,
          section: section.label,
          path: item.path,
          icon: item.icon,
          keywords: [item.label, section.label, item.path, "chart", "vitals", "labs", "allergy", "allergies", "meds", "medications"],
        })),
      ),
    [],
  );

  const filteredTargets = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (!term) return chartTargets;
    return chartTargets.filter((target) =>
      `${target.label} ${target.section} ${target.keywords.join(" ")}`.toLowerCase().includes(term),
    );
  }, [chartTargets, searchQuery]);

  useEffect(() => {
    setActiveIndex(0);
  }, [searchQuery, searchOpen]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const goToChartSection = (path: string) => {
    setSearchOpen(false);
    setSearchQuery("");
    navigate(chartHref(id, path));
  };

  const onSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(filteredTargets.length - 1, 0)));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (event.key === "Enter" && filteredTargets[activeIndex]) {
      event.preventDefault();
      goToChartSection(filteredTargets[activeIndex].path);
    }
  };

  return (
    <>
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-3 sm:px-5">
        {/* Patient identity (the side card shows it on wide screens) */}
        <div className="flex min-w-0 items-center gap-3 lg:hidden">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#ff8d6e] text-xs font-semibold text-[#003a43]">
            {photoSrc ? <img src={photoSrc} alt="" className="h-full w-full object-cover" /> : patient ? initials : <User className="h-4 w-4" />}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">{displayName ?? "Opening patient record…"}</p>
            <p className="truncate text-xs text-muted-foreground">{[ageLabel, mrnLabel].filter(Boolean).join(" · ")}</p>
          </div>
        </div>
        <p className="hidden text-xs font-semibold uppercase tracking-[0.16em] text-[#c4532f] lg:block">Patient chart</p>

        <button
          type="button"
          onClick={() => setSearchOpen(true)}
          className="ml-auto flex h-10 w-10 items-center justify-center gap-2 rounded-full bg-[#f8f3eb] text-sm text-muted-foreground transition-colors hover:bg-[#f1ece2] dark:bg-muted sm:w-72 sm:justify-start sm:px-4"
          aria-label="Search this chart"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="hidden flex-1 truncate text-left sm:inline">Search this chart</span>
          <kbd className="hidden items-center gap-0.5 rounded-md bg-card px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground sm:inline-flex">
            <Command className="h-3 w-3" />K
          </kbd>
        </button>

        {alertCount > 0 && (
          <Tooltip delayDuration={80}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() =>
                  navigate(uniqueAllergies.length > 0 ? `/patients/${id}/allergies` : `/patients/${id}/settings?tab=announcements`)
                }
                className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#ffe9e1] text-[#c4532f] transition-colors hover:bg-[#ffdccf] dark:bg-[#ff8d6e]/15"
                aria-label={`${alertCount} patient alert${alertCount !== 1 ? "s" : ""}`}
              >
                <AlertCircle className="h-[18px] w-[18px]" />
                <span className="absolute -right-0.5 -top-0.5 flex h-4.5 min-w-4.5 items-center justify-center rounded-full bg-[#ff8d6e] px-1 text-[10px] font-bold text-[#003a43] ring-2 ring-background">
                  {alertCount > 9 ? "9+" : alertCount}
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {uniqueAllergies.length > 0 ? `${uniqueAllergies.length} allerg${uniqueAllergies.length === 1 ? "y" : "ies"}` : null}
              {uniqueAllergies.length > 0 && activeAnnouncements.length > 0 ? " · " : null}
              {activeAnnouncements.length > 0
                ? `${activeAnnouncements.length} staff alert${activeAnnouncements.length !== 1 ? "s" : ""}`
                : null}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Chart menu (small screens) */}
      <div className="flex shrink-0 gap-1.5 overflow-x-auto border-b border-border px-3 py-2.5 scrollbar-none lg:hidden">
        {CHART_NAV.flatMap((section) => section.items).map((item) => (
          <ChartNavLink
            key={item.path || "summary"}
            href={chartHref(id, item.path)}
            end={item.end}
            icon={item.icon}
            label={item.label}
            compact
          />
        ))}
      </div>

      <Dialog
        open={searchOpen}
        onOpenChange={(open) => {
          setSearchOpen(open);
          if (!open) setSearchQuery("");
        }}
      >
        <DialogContent className="max-w-lg gap-0 overflow-hidden rounded-[20px] border-border p-0 shadow-xl">
          <DialogHeader className="border-b border-border px-5 pb-3 pt-4">
            <DialogTitle className="flex items-center gap-2 text-base font-semibold">
              <Search className="h-4 w-4 text-[#c4532f]" />
              Search this chart
            </DialogTitle>
          </DialogHeader>
          <div className="border-b border-border px-4 py-3">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                autoFocus
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder="Vitals, allergies, encounters…"
                className="h-11 w-full rounded-full bg-[#f8f3eb] pl-11 pr-14 text-sm outline-none ring-primary/20 focus:ring-4 dark:bg-muted"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md bg-card px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                Esc
              </span>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto p-2">
            {filteredTargets.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                No chart sections match “{searchQuery.trim()}”
              </p>
            ) : (
              filteredTargets.map((target, index) => {
                const Icon = target.icon;
                const isActive = index === activeIndex;
                return (
                  <button
                    key={target.id}
                    type="button"
                    onClick={() => goToChartSection(target.path)}
                    onMouseEnter={() => setActiveIndex(index)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
                      isActive ? "bg-muted" : "hover:bg-muted",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-9 w-9 items-center justify-center rounded-full",
                        isActive ? "bg-[#003a43] text-white" : "bg-[#f1ece2] text-[#004952] dark:bg-card dark:text-foreground",
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground">{target.label}</span>
                      <span className="block text-xs text-muted-foreground">{target.section}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Root layout ───────────────────────────────────────────────────────────────
/** Patient chart on the same frame as the main app: floating header, side card, content panel. */
export default function PatientChartLayout() {
  const { id = "" } = useParams<{ id: string }>();
  const { user } = useAuth();

  return (
    <div className="app-shell relative flex h-screen flex-col overflow-hidden bg-[var(--canvas)]">
      <FloatingHeader user={user} />

      <div className="flex min-h-0 flex-1 gap-2 px-3 pb-3 sm:px-4">
        <ChartSideColumn id={id} />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[24px] bg-background shadow-[0_8px_30px_-12px_rgba(0,58,67,0.25)] ring-1 ring-[#d6cdbb] dark:ring-border">
          <ChartPatientBar id={id} />
          <WorkbenchTabBarSlot />
          <PatientAnnouncementBanner patientId={id} />
          <main className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin">
            <div className="mx-auto max-w-[1600px] p-4 pb-16 sm:p-6">
              <ShellPageTransition>
                <Outlet />
              </ShellPageTransition>
            </div>
          </main>
          <WorkbenchFloatingDock />
        </div>
      </div>
    </div>
  );
}
